import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { msalInstance, msalReady } from "@/lib/msal-config";
import { useBookingsCalendarStore } from "@/store/BookingsCalendarStore";
import { useBookingsCurrentStore } from "@/store/BookingsCurrentStore";

// ---------------------- Variables

const enableStore = import.meta.env.VITE_CURRENT_MODE !== "production";

// ---------------------- Interfaces

export interface AuthState {
  // --------- State

  userResourceId: string | null;
  userSystemId: string | null;
  userName: string | null;
  userEmail: string | null;

  // --------- Actions

  setUserResourceId: (id: string | null) => void;
  setUserSystemId: (id: string | null) => void;
  setUserName: (name: string | null) => void;
  setUserEmail: (email: string | null) => void;
  signOut: () => Promise<void>;
}

// ---------------------- Store

export const useAuthStore = create<AuthState>()(
  devtools(
    immer((set) => ({
      // --------- State

      userResourceId: null as string | null,
      userSystemId: null as string | null,
      userName: null as string | null,
      userEmail: null as string | null,

      // --------- Actions

      setUserResourceId: (id: string | null) =>
        set(
          (state) => {
            state.userResourceId = id;
          },
          false,
          "auth/setUserResourceId",
        ),

      setUserSystemId: (id: string | null) =>
        set(
          (state) => {
            state.userSystemId = id;
          },
          false,
          "auth/setUserSystemId",
        ),

      setUserName: (name: string | null) =>
        set(
          (state) => {
            state.userName = name;
          },
          false,
          "auth/setUserName",
        ),

      setUserEmail: (email: string | null) =>
        set(
          (state) => {
            state.userEmail = email;
          },
          false,
          "auth/setUserEmail",
        ),

      signOut: async () => {
        // Reset all stores before clearing MSAL cache
        useBookingsCurrentStore.getState().reset();
        useBookingsCalendarStore.getState().reset();

        set(
          (state) => {
            state.userResourceId = null;
            state.userSystemId = null;
            state.userName = null;
            state.userEmail = null;
          },
          false,
          "auth/signOut",
        );

        // Clear local token cache only — preserves Microsoft 365 session for quick re-login
        await msalReady;
        await msalInstance.clearCache();

        window.location.href = "/login";
      },
    })),
    {
      name: "AuthStore",
      enabled: enableStore,
      store: "AuthStore",
    },
  ),
);
